import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { BanksService, type Bank } from '@/services/modules/BanksService'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

export function SettingsBanksPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.banks.create')
  const canEdit = can('settings.banks.edit')
  const canDelete = can('settings.banks.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar banco' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(BanksService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: Bank }>({ open: false })
  const [openDelete, setOpenDelete] = useState<Bank | null>(null)

  const exp = useDataTableExport({
    filename: 'bancos',
    service: BanksService,
    filters: sf.filters,
    columns: [
      { header: 'Código',       accessor: (b: Bank) => b.code },
      { header: 'Nome',         accessor: (b: Bank) => b.name },
      { header: 'Razão social', accessor: (b: Bank) => b.fullName ?? '' },
      { header: 'Status',       accessor: (b: Bank) => b.status },
    ],
  })

  async function handleSave(payload: Partial<Bank>) {
    try {
      if (openForm.item) { await BanksService.update(openForm.item.id, payload); toast.success('Banco atualizado.') }
      else { await BanksService.create(payload); toast.success('Banco criado.') }
      setOpenForm({ open: false }); setReload(v => v + 1)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.') }
  }
  async function handleDelete() {
    if (!openDelete) return
    try { await BanksService.destroy(openDelete.id); toast.success('Banco removido.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  // Primeira coluna: código + nome combinados. Demais center. Ações vão na coluna fixa do dropdown (direita).
  const columns: DataTableColumn<Bank>[] = [
    {
      key: 'main',
      header: 'Banco',
      sortable: true,
      sortKey: 'name',
      alwaysVisible: true,
      render: b => (
        <div>
          <div className="font-medium text-gray-800">{b.code} — {b.name}</div>
          {b.fullName && <div className="text-[11px] text-gray-500">{b.fullName}</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: b => (
        <StatusDot value={b.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Bancos"
        subtitle="Lista de bancos cadastrados (FEBRABAN)"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Workflow' }, { label: 'Bancos' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo banco</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.workflow.banks"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={b => b.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(b) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />,  onClick: () => setOpenForm({ open: true, item: b }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />,  danger: true, divider: canEdit, onClick: () => setOpenDelete(b) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <BankForm open={openForm.open} item={openForm.item} onClose={() => setOpenForm({ open: false })} onSave={handleSave} />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover banco?"
        description={`Tem certeza que deseja remover "${openDelete?.code} — ${openDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function BankForm({ open, item, onClose, onSave }: {
  open: boolean; item?: Bank; onClose: () => void
  onSave: (payload: Partial<Bank>) => Promise<void>
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [fullName, setFullName] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setCode(item?.code ?? '')
    setName(item?.name ?? '')
    setFullName(item?.fullName ?? '')
    setStatus(item?.status ?? 'ATIVO')
  }, [open, item])

  async function submit() {
    setSaving(true)
    await onSave({ code, name, fullName: fullName || null, status } as Partial<Bank>)
    setSaving(false)
  }
  return (
    <Modal open={open} onClose={onClose}
      title={item ? 'Editar banco' : 'Novo banco'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!code.trim() || !name.trim()}>Salvar</Button></>}
    >
      <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3">
        <Input label="Código" required value={code} onChange={e => setCode(e.target.value)} placeholder="001" />
        <Input label="Nome"   required value={name} onChange={e => setName(e.target.value)} placeholder="Banco do Brasil" />
        <Input label="Razão social" value={fullName} onChange={e => setFullName(e.target.value)} wrapperClassName="md:col-span-2" placeholder="Banco do Brasil S.A." />
        <div className="md:col-span-2">
          <StatusToggle value={status} onChange={setStatus} />
        </div>
      </div>
    </Modal>
  )
}
